import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
    BookOpen, CheckSquare, Bell, Shield, BarChart3,
    AlertTriangle, Filter, ChevronDown, ChevronUp,
    ArrowRight, RotateCcw, Search, Clock, Users,
    CheckCircle2, Circle, Send, MoreVertical, Copy, Trash2, Edit3
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import AnalyticsDrawer from '../components/AnalyticsDrawer';
import OverviewAnalyticsDrawer from '../components/Analytics/OverviewAnalyticsDrawer';
import GroupAnalyticsDrawer from '../components/Analytics/GroupAnalyticsDrawer';
import TrendsAnalyticsDrawer from '../components/Analytics/TrendsAnalyticsDrawer';

// ═══════════════════════════════════════════════════════════
//  DESIGN TOKENS — Consistent UI Patterns
// ═══════════════════════════════════════════════════════════
const TOKENS = {
    // Colors
    textPrimary: 'var(--foreground)',
    textSecondary: 'var(--muted-foreground)',
    textMuted: 'var(--muted-foreground)',
    accent: 'var(--accent)',
    accentHover: 'var(--accent-light)',
    accentBg: '#FEF2EE',
    border: 'var(--border)',
    borderLight: 'var(--border)',
    bgPage: 'var(--background)',
    bgCard: 'var(--card)',
    bgHover: 'var(--secondary)',
    warning: 'var(--warning)',
    warningBg: '#FEF3C7',
    warningText: '#92400E',

    // Status colors
    success: '#10B981',
    successBg: '#ECFDF5',
    successText: '#065F46',
    info: '#3B82F6',
    infoBg: '#EFF6FF',
    infoText: '#1E40AF',
    danger: '#EF4444',
    dangerBg: '#FEF2F2',
    dangerText: '#991B1B',
    neutral: '#6B7280',
    neutralBg: '#F3F4F6',
    neutralText: '#374151',

    // Card
    cardBorder: '1px solid var(--border)',
    cardRadius: '8px',
    cardPadding: '16px 20px',
    cardMarginBottom: '12px',

    // Filter Bar
    filterHeight: '36px',
    filterRadius: '4px',
    filterPadding: '8px 12px',

    // Tabs
    tabHeight: '40px',
    tabFont: '14px',
    tabSpacing: '24px',

    // Buttons
    btnHeight: '36px',
    btnRadius: '6px',
    btnPadding: '8px 16px',
};

// Activity type configuration
const ACTIVITY_TYPES = {
    task: { icon: CheckSquare, label: 'Tasks', color: '#E86F4A', emoji: '✅' },
    course: { icon: BookOpen, label: 'Courses', color: '#2A3F54', emoji: '📚' },
    notice: { icon: Bell, label: 'Notices', color: '#626B7A', emoji: '📢' },
    compliance: { icon: Shield, label: 'Compliance', color: '#2A3F54', emoji: '⚖️' },
    survey: { icon: BarChart3, label: 'Surveys', color: '#EE9072', emoji: '📊' },
};

const STATUS_TABS = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'pending_review', label: 'Pending Review' },
    { key: 'completed', label: 'Completed' },
    { key: 'overdue', label: 'Overdue' },
];

const SORT_OPTIONS = [
    { key: 'updated', label: 'Recently Updated' },
    { key: 'created', label: 'Recently Created' },
    { key: 'most_overdue', label: 'Most Overdue' },
    { key: 'lowest_completion', label: 'Lowest Completion' },
    { key: 'most_assigned', label: 'Most Assigned' },
];

const EMPTY_STATE_MESSAGES = {
    all: { title: 'No Activities Yet', desc: 'Create tasks, courses, notices, or surveys to see them here.' },
    active: { title: 'No Active Activities', desc: 'Activities appear here when at least one employee hasn\'t completed them yet.' },
    pending_review: { title: 'No Pending Reviews', desc: 'Activities with proof uploads or e-signatures awaiting admin review will appear here.' },
    completed: { title: 'No Completed Activities', desc: 'Activities appear here when 100% of assigned employees have completed them.' },
    overdue: { title: 'Nothing Overdue!', desc: 'All activities are on track. Great work! 🎉' },
};


// ═══════════════════════════════════════════════════════════
//  PROGRESS BAR
// ═══════════════════════════════════════════════════════════
const ProgressBar = ({ completed, total, percentage, hasOverdue }) => {
    if (total === 0 || total == null) return null;

    const barColor = hasOverdue ? '#F59E0B' : TOKENS.success;

    return (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: TOKENS.textSecondary }}>
                    {completed}/{total} completed
                </span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: barColor }}>
                    {percentage}%
                </span>
            </div>
            <div style={{
                height: '6px',
                backgroundColor: 'var(--secondary)',
                borderRadius: '3px',
                overflow: 'hidden',
            }}>
                <div style={{
                    height: '100%',
                    width: `${percentage}%`,
                    backgroundColor: barColor,
                    borderRadius: '3px',
                    transition: 'width 0.5s ease',
                    minWidth: percentage > 0 ? '4px' : '0',
                }} />
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
//  STATUS BREAKDOWN PILLS
// ═══════════════════════════════════════════════════════════
const StatusBreakdown = ({ item }) => {
    const pills = [];

    if (item.completed_count > 0) {
        pills.push({
            icon: CheckCircle2,
            label: `${item.completed_count} Completed`,
            bg: TOKENS.successBg,
            color: TOKENS.successText,
        });
    }
    if (item.in_progress_count > 0) {
        pills.push({
            icon: Clock,
            label: `${item.in_progress_count} In Progress`,
            bg: TOKENS.infoBg,
            color: TOKENS.infoText,
        });
    }
    if (item.overdue_count > 0) {
        pills.push({
            icon: AlertTriangle,
            label: `${item.overdue_count} Overdue`,
            bg: TOKENS.dangerBg,
            color: TOKENS.dangerText,
        });
    }
    if (item.pending_review_count > 0) {
        pills.push({
            icon: Clock,
            label: `${item.pending_review_count} Pending Review`,
            bg: TOKENS.warningBg,
            color: TOKENS.warningText,
        });
    }
    if (item.not_started_count > 0) {
        pills.push({
            icon: Circle,
            label: `${item.not_started_count} Not Started`,
            bg: TOKENS.neutralBg,
            color: TOKENS.neutralText,
        });
    }

    if (pills.length === 0) return null;

    return (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {pills.map((pill, i) => (
                <span
                    key={i}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: '12px',
                        backgroundColor: pill.bg,
                        color: pill.color,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <pill.icon size={11} />
                    {pill.label}
                </span>
            ))}
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
//  QUICK ACTIONS MENU
// ═══════════════════════════════════════════════════════════
const QuickActionsMenu = ({ item, onEdit, onSendReminder }) => {
    const [open, setOpen] = useState(false);

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: TOKENS.btnRadius,
                    background: 'transparent',
                    cursor: 'pointer',
                    color: TOKENS.textSecondary,
                    transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
                <MoreVertical size={14} />
            </button>
            {open && (
                <>
                    <div
                        style={{ position: 'fixed', inset: 0, zIndex: 100 }}
                        onClick={() => setOpen(false)}
                    />
                    <div style={{
                        position: 'absolute',
                        right: 0,
                        top: '100%',
                        marginTop: '4px',
                        background: TOKENS.bgCard,
                        border: `1px solid ${TOKENS.border}`,
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 101,
                        minWidth: '140px',
                        overflow: 'hidden',
                    }}>
                        {[
                            { icon: Edit3, label: 'Edit', onClick: () => { onEdit?.(item); setOpen(false); } },
                            { icon: Copy, label: 'Duplicate', onClick: () => { toast.success(`"${item.title}" duplicated`); setOpen(false); } },
                            { icon: Trash2, label: 'Delete', color: TOKENS.danger, onClick: () => { toast.success(`"${item.title}" deleted`); setOpen(false); } },
                        ].map((action, i) => (
                            <button
                                key={i}
                                onClick={(e) => { e.stopPropagation(); action.onClick(); }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    color: action.color || TOKENS.textPrimary,
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <action.icon size={14} />
                                {action.label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
//  ACTIVITY CARD (Redesigned)
// ═══════════════════════════════════════════════════════════
const ActivityCard = ({ item, onViewStats, onEdit, onSendReminder }) => {
    const [isHovered, setIsHovered] = useState(false);
    const typeConfig = ACTIVITY_TYPES[item.type] || ACTIVITY_TYPES.task;
    const hasAssignment = item.assigned_count != null && item.assigned_count > 0;
    const isNotice = item.type === 'notice';

    // Urgency tier
    const getUrgencyColor = () => {
        if (!hasAssignment) return 'transparent';
        const overdueRatio = item.overdue_count / item.assigned_count;
        const completionPct = item.completion_percentage || 0;
        if (overdueRatio >= 0.3 || completionPct < 25) return TOKENS.danger;
        if (item.overdue_count > 0) return '#F59E0B';
        return TOKENS.success;
    };

    const urgencyColor = getUrgencyColor();

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        if (dateStr === 'rolling') return 'Rolling';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div
            style={{
                background: TOKENS.bgCard,
                borderTop: `1px solid ${isHovered ? TOKENS.accent : TOKENS.border}`,
                borderRight: `1px solid ${isHovered ? TOKENS.accent : TOKENS.border}`,
                borderBottom: `1px solid ${isHovered ? TOKENS.accent : TOKENS.border}`,
                borderLeft: hasAssignment ? `3px solid ${urgencyColor}` : `1px solid ${isHovered ? TOKENS.accent : TOKENS.border}`,
                borderRadius: TOKENS.cardRadius,
                padding: TOKENS.cardPadding,
                marginBottom: TOKENS.cardMarginBottom,
                transition: 'all 0.2s ease',
                boxShadow: isHovered ? '0 2px 8px rgba(232, 111, 74, 0.12)' : 'none',
                cursor: 'pointer',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => onViewStats?.(item)}
        >
            {/* Row 1: Icon + Title + Type Badge + Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                {item.overdue_count > 0 ? (
                    <AlertTriangle size={18} style={{ color: TOKENS.warning, flexShrink: 0 }} />
                ) : (
                    <typeConfig.icon size={18} style={{ color: typeConfig.color, flexShrink: 0 }} />
                )}
                <span style={{ fontWeight: 600, fontSize: '14px', color: TOKENS.textPrimary, flex: 1 }}>
                    {item.title}
                </span>
                <span
                    style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '12px',
                        backgroundColor: typeConfig.color + '15',
                        color: typeConfig.color,
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                        flexShrink: 0,
                    }}
                >
                    {typeConfig.label}
                </span>
            </div>

            {/* Row 2: Lifecycle + Assigned count meta */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: TOKENS.textSecondary, marginBottom: '10px', flexWrap: 'wrap' }}>
                {item.lifecycle && (
                    <span>Lifecycle: <strong style={{ color: TOKENS.textPrimary }}>{item.lifecycle}</strong></span>
                )}
                {hasAssignment && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={12} />
                        <strong style={{ color: TOKENS.textPrimary }}>{item.assigned_count}</strong> assigned
                    </span>
                )}
                {isNotice && (
                    <span>Views: <strong style={{ color: TOKENS.textPrimary }}>{item.completed_count}</strong></span>
                )}
            </div>

            {/* Row 3: Progress Bar (only for activities with assignments) */}
            {hasAssignment && !isNotice && (
                <ProgressBar
                    completed={item.completed_count}
                    total={item.assigned_count}
                    percentage={item.completion_percentage}
                    hasOverdue={item.overdue_count > 0}
                />
            )}

            {/* Row 4: Status Breakdown Pills */}
            {hasAssignment && !isNotice && (
                <StatusBreakdown item={item} />
            )}

            {/* Row 5: Dates + Actions */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: TOKENS.textMuted }}>
                    {item.updated_at && <span>Updated: {formatDate(item.updated_at)}</span>}
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {/* Send Reminder — only if overdue */}
                    {item.overdue_count > 0 && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onSendReminder?.(item); }}
                            style={{
                                height: '30px',
                                padding: '4px 12px',
                                fontSize: '12px',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                border: `1px solid ${TOKENS.warning}`,
                                borderRadius: TOKENS.btnRadius,
                                background: TOKENS.warningBg,
                                color: TOKENS.warningText,
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                        >
                            <Send size={12} />
                            Remind
                        </button>
                    )}
                    {/* View Stats Button */}
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewStats?.(item); }}
                        style={{
                            height: '30px',
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: TOKENS.btnRadius,
                            background: 'transparent',
                            color: TOKENS.textPrimary,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                        View Stats
                    </button>
                    {/* Quick Actions Menu */}
                    <QuickActionsMenu item={item} onEdit={onEdit} onSendReminder={onSendReminder} />
                </div>
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════
//  MAIN OVERVIEW COMPONENT
// ═══════════════════════════════════════════════════════════
const ActivityCentreOverview = () => {
    const location = useLocation();
    const initialTab = location.state?.tab || 'all';
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [drawerContext, setDrawerContext] = useState(null);
    const [showOverviewAnalytics, setShowOverviewAnalytics] = useState(false);
    const [showGroupAnalytics, setShowGroupAnalytics] = useState(false);
    const [showTrendsAnalytics, setShowTrendsAnalytics] = useState(false);

    // Filters
    const [typeFilters, setTypeFilters] = useState({
        task: true, course: true, notice: true, compliance: true, survey: true,
    });
    const [lifecycleFilter, setLifecycleFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('updated');

    const fetchActivities = useCallback(async () => {
        setLoading(true);
        try {
            const enabledTypes = Object.entries(typeFilters)
                .filter(([, v]) => v)
                .map(([k]) => k)
                .join(',');

            const params = new URLSearchParams();
            if (enabledTypes) params.append('types', enabledTypes);
            if (lifecycleFilter !== 'all') params.append('lifecycle', lifecycleFilter);

            const res = await api.get(`/activity-centre/overview?${params}`);
            setActivities(res.data.activities || []);
        } catch (err) {
            console.error('Failed to fetch activities:', err);
            setActivities([]);
        } finally {
            setLoading(false);
        }
    }, [typeFilters, lifecycleFilter]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    // ─── Multi-tab filtering (client-side) ───
    const filterByTab = useCallback((items, tab) => {
        switch (tab) {
            case 'all':
                return items;
            case 'active':
                return items.filter(a => {
                    // Has assignments and not 100% complete
                    if (a.type === 'notice') return a.status === 'active' || a.status === 'published';
                    return a.assigned_count > 0 && a.completed_count < a.assigned_count;
                });
            case 'pending_review':
                return items.filter(a => (a.pending_review_count || 0) > 0);
            case 'completed':
                return items.filter(a => {
                    if (a.type === 'notice') return false; // Notices don't have completion tracking
                    return a.assigned_count > 0 && a.completed_count === a.assigned_count;
                });
            case 'overdue':
                return items.filter(a => (a.overdue_count || 0) > 0);
            default:
                return items;
        }
    }, []);

    // ─── Search filter ───
    const searchFiltered = useMemo(() => {
        if (!searchQuery.trim()) return activities;
        const q = searchQuery.toLowerCase();
        return activities.filter(a => a.title.toLowerCase().includes(q));
    }, [activities, searchQuery]);

    // ─── Tab counts (computed from search-filtered activities) ───
    const tabCounts = useMemo(() => {
        const counts = {};
        STATUS_TABS.forEach(tab => {
            counts[tab.key] = filterByTab(searchFiltered, tab.key).length;
        });
        return counts;
    }, [searchFiltered, filterByTab]);

    // ─── Sort ───
    const sortedAndFiltered = useMemo(() => {
        let items = filterByTab(searchFiltered, activeTab);

        switch (sortBy) {
            case 'most_overdue':
                items = [...items].sort((a, b) => (b.overdue_count || 0) - (a.overdue_count || 0));
                break;
            case 'created':
                items = [...items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                break;
            case 'lowest_completion':
                items = [...items].sort((a, b) => (a.completion_percentage || 0) - (b.completion_percentage || 0));
                break;
            case 'most_assigned':
                items = [...items].sort((a, b) => (b.assigned_count || 0) - (a.assigned_count || 0));
                break;
            default: // 'updated'
                items = [...items].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        }

        return items;
    }, [searchFiltered, activeTab, sortBy, filterByTab]);

    const toggleTypeFilter = (type) => {
        setTypeFilters(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const resetFilters = () => {
        setTypeFilters({ task: true, course: true, notice: true, compliance: true, survey: true });
        setLifecycleFilter('all');
        setActiveTab('all');
        setSearchQuery('');
        setSortBy('updated');
    };

    const handleViewStats = (item) => {
        setDrawerContext({ type: item.type, id: item.id, title: item.title });
    };

    const handleEdit = (item) => {
        const routeMap = {
            task: '/admin/activity-centre/tasks',
            course: '/admin/activity-centre/courses',
            notice: '/admin/activity-centre/notices',
            compliance: '/admin/activity-centre/compliance',
            survey: '/admin/activity-centre/surveys',
        };
        window.location.href = routeMap[item.type] || '/admin/activity-centre';
    };

    const handleSendReminder = (item) => {
        toast.success(`Reminder sent for "${item.title}"`, { duration: 2000 });
    };

    // ─── Shared styles ───
    const dropdownStyle = {
        height: TOKENS.filterHeight,
        padding: TOKENS.filterPadding,
        fontSize: '13px',
        border: `1px solid ${TOKENS.border}`,
        borderRadius: TOKENS.filterRadius,
        background: TOKENS.bgCard,
        color: TOKENS.textPrimary,
        cursor: 'pointer',
        outline: 'none',
    };

    const emptyState = EMPTY_STATE_MESSAGES[activeTab] || EMPTY_STATE_MESSAGES.all;

    return (
        <div style={{ padding: '24px 32px', maxWidth: '1100px', margin: '0 auto' }}>
            {/* ───────────── Header ───────────── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '24px', fontWeight: 700, color: TOKENS.textPrimary, margin: 0 }}>
                        Activity Centre
                    </h1>
                    <p style={{ fontSize: '14px', color: TOKENS.textSecondary, marginTop: '4px' }}>
                        Manage courses, tasks, notices, and compliance items
                    </p>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            height: TOKENS.btnHeight,
                            padding: TOKENS.btnPadding,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: TOKENS.textPrimary,
                            background: TOKENS.bgCard,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: TOKENS.btnRadius,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.bgCard; }}
                        onClick={() => setShowOverviewAnalytics(true)}
                    >
                        <BarChart3 size={14} /> View Dashboard Analytics
                    </button>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            height: TOKENS.btnHeight,
                            padding: TOKENS.btnPadding,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: TOKENS.textPrimary,
                            background: TOKENS.bgCard,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: TOKENS.btnRadius,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.bgCard; }}
                        onClick={() => setShowGroupAnalytics(true)}
                    >
                        <Users size={14} /> Group Analytics
                    </button>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            height: TOKENS.btnHeight,
                            padding: TOKENS.btnPadding,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: TOKENS.textPrimary,
                            background: TOKENS.bgCard,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: TOKENS.btnRadius,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.bgCard; }}
                        onClick={() => setShowTrendsAnalytics(true)}
                    >
                        <BarChart3 size={14} /> Trends & History
                    </button>
                    <button
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            height: TOKENS.btnHeight,
                            padding: TOKENS.btnPadding,
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#FFFFFF',
                            background: TOKENS.accent,
                            border: 'none',
                            borderRadius: TOKENS.btnRadius,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.accentHover; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = TOKENS.accent; }}
                        onClick={() => setDrawerContext({ type: 'overview' })}
                    >
                        View Insights <ArrowRight size={14} />
                    </button>
                </div>
            </div>

            {/* ───────────── Status Tabs with Counts ───────────── */}
            <div style={{
                display: 'flex',
                gap: TOKENS.tabSpacing,
                borderBottom: `2px solid ${TOKENS.borderLight}`,
                marginBottom: '20px',
            }}>
                {STATUS_TABS.map(tab => {
                    const isActive = activeTab === tab.key;
                    const count = tabCounts[tab.key] || 0;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            style={{
                                height: TOKENS.tabHeight,
                                padding: '0 4px',
                                fontSize: TOKENS.tabFont,
                                fontWeight: 500,
                                color: isActive ? TOKENS.textPrimary : TOKENS.textSecondary,
                                background: 'none',
                                border: 'none',
                                borderBottom: isActive ? `3px solid ${TOKENS.accent}` : '3px solid transparent',
                                marginBottom: '-2px',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive) {
                                    e.currentTarget.style.color = TOKENS.textPrimary;
                                    e.currentTarget.style.background = TOKENS.bgHover;
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive) {
                                    e.currentTarget.style.color = TOKENS.textSecondary;
                                    e.currentTarget.style.background = 'none';
                                }
                            }}
                        >
                            {tab.label}
                            {!loading && (
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    backgroundColor: isActive ? TOKENS.accent + '20' : TOKENS.bgHover,
                                    color: isActive ? TOKENS.accent : TOKENS.textSecondary,
                                    padding: '1px 7px',
                                    borderRadius: '10px',
                                    minWidth: '20px',
                                    textAlign: 'center',
                                }}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* ───────────── Search + Sort Bar ───────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
            }}>
                {/* Search Box */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flex: 1,
                    height: TOKENS.filterHeight,
                    padding: '0 12px',
                    border: `1px solid ${TOKENS.border}`,
                    borderRadius: TOKENS.filterRadius,
                    background: TOKENS.bgCard,
                }}>
                    <Search size={14} style={{ color: TOKENS.textSecondary, flexShrink: 0 }} />
                    <input
                        type="text"
                        placeholder="Search activities..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            flex: 1,
                            border: 'none',
                            outline: 'none',
                            fontSize: '13px',
                            color: TOKENS.textPrimary,
                            background: 'transparent',
                        }}
                    />
                </div>

                {/* Sort Dropdown */}
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={dropdownStyle}
                >
                    {SORT_OPTIONS.map(opt => (
                        <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* ───────────── Filter Bar ───────────── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                height: 'auto',
                minHeight: TOKENS.filterHeight,
                padding: '10px 16px',
                background: TOKENS.bgCard,
                border: `1px solid ${TOKENS.border}`,
                borderRadius: TOKENS.filterRadius,
                marginBottom: '24px',
                flexWrap: 'wrap',
            }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: TOKENS.textSecondary, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Filter size={14} />
                    Filters:
                </span>

                {/* Type checkboxes */}
                {Object.entries(ACTIVITY_TYPES).map(([key, config]) => (
                    <label
                        key={key}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            color: TOKENS.textPrimary,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={typeFilters[key]}
                            onChange={() => toggleTypeFilter(key)}
                            style={{ accentColor: TOKENS.accent }}
                        />
                        {config.label}
                    </label>
                ))}

                <div style={{ width: '1px', height: '20px', background: TOKENS.border }} />

                {/* Lifecycle dropdown */}
                <select
                    value={lifecycleFilter}
                    onChange={(e) => setLifecycleFilter(e.target.value)}
                    style={dropdownStyle}
                >
                    <option value="all">All Lifecycles</option>
                    <option value="preboarding">Preboarding</option>
                    <option value="onboarding">Onboarding</option>
                </select>

                {/* Reset */}
                <button
                    onClick={resetFilters}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        height: TOKENS.filterHeight,
                        padding: TOKENS.filterPadding,
                        fontSize: '12px',
                        fontWeight: 500,
                        color: TOKENS.textSecondary,
                        background: 'transparent',
                        border: `1px solid ${TOKENS.border}`,
                        borderRadius: TOKENS.filterRadius,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = TOKENS.bgHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                    <RotateCcw size={12} /> Reset
                </button>
            </div>

            {/* ───────────── Content ───────────── */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: TOKENS.textSecondary }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: `3px solid ${TOKENS.borderLight}`,
                        borderTop: `3px solid ${TOKENS.accent}`,
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 12px',
                    }} />
                    <p style={{ fontSize: '14px' }}>Loading activities...</p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : sortedAndFiltered.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: TOKENS.textSecondary }}>
                    <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: TOKENS.bgHover,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                    }}>
                        {activeTab === 'overdue' ? (
                            <CheckCircle2 size={24} style={{ color: TOKENS.success }} />
                        ) : (
                            <ClipboardListIcon size={24} style={{ color: TOKENS.textSecondary }} />
                        )}
                    </div>
                    <p style={{ fontSize: '16px', fontWeight: 600, color: TOKENS.textPrimary, marginBottom: '4px' }}>
                        {emptyState.title}
                    </p>
                    <p style={{ fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
                        {emptyState.desc}
                    </p>
                </div>
            ) : (
                <div>
                    {sortedAndFiltered.map((item, idx) => (
                        <ActivityCard
                            key={`${item.type}-${item.id}-${idx}`}
                            item={item}
                            onViewStats={handleViewStats}
                            onEdit={handleEdit}
                            onSendReminder={handleSendReminder}
                        />
                    ))}
                </div>
            )}

            {/* Analytics Drawer */}
            <AnalyticsDrawer
                isOpen={!!drawerContext}
                onClose={() => setDrawerContext(null)}
                context={drawerContext}
            />

            <OverviewAnalyticsDrawer
                isOpen={showOverviewAnalytics}
                onClose={() => setShowOverviewAnalytics(false)}
            />

            <GroupAnalyticsDrawer
                isOpen={showGroupAnalytics}
                onClose={() => setShowGroupAnalytics(false)}
            />

            <TrendsAnalyticsDrawer
                isOpen={showTrendsAnalytics}
                onClose={() => setShowTrendsAnalytics(false)}
            />
        </div>
    );
};

// Simple fallback icon for empty state
const ClipboardListIcon = ({ size, style }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={style}
    >
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        <path d="M12 11h4" />
        <path d="M12 16h4" />
        <path d="M8 11h.01" />
        <path d="M8 16h.01" />
    </svg>
);

export default ActivityCentreOverview;
